<template>
  <div class="w-full px-8 py-4" data-testid="leads-event-section">
    <div v-if="!loading" class="flex">
      <div>
        <div class="flex flex-col items-center">
          <span class="mb-3 txt-body-s-regular">{{ t('EventsLeadsCount.title') }}</span>
          <span class="txt-display-l-bold" data-testid="leads-total-count">{{ sumOfLeads }}</span>
          <span class="mt-3 txt-body-s-regular">{{ t('EventsLeadsCount.subtitle', { eventName: rootEventName }) }}</span>
        </div>
      </div>
      <div v-if="false" class="flex justify-center">
        <p>PIE CHART HERE</p>
      </div>
    </div>
    <hr class="my-6" />

    <div v-if="!loading" class="divide-y divide-(--border-color-main)" data-testid="leads-event-list">
      <div v-if="hasPartnerCompany && partnerCompanyAppLeadCount && rootEvent">
        <EventLeadsItem
          title="OMR App"
          :starts-at="rootEvent.startsAt"
          :ends-at="rootEvent.endsAt"
          :lead-count="partnerCompanyAppLeadCount"
          :download-url="downloadAppLeadsUrl()"
        />
      </div>
      <div v-for="event in events" :key="event?.id">
        <EventLeadsItem
          v-if="event"
          :title="`${event.name}`"
          :starts-at="event.startsAt"
          :ends-at="event.endsAt"
          :lead-count="event.leadCount"
          :download-url="downloadEventLeadsUrl(event.id)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import {
  useLeadCountsByEventQuery,
  EventTypeEnum,
  useEventBySlugQuery,
  type LeadCountsByEventQuery,
  usePartnerCompanyLeadCountQuery,
  type LeadCountsByEventQueryVariables,
} from '@/gql/myomr'
import { toTypeAndId } from '@/helpers/globalIdHelper'
import { type exportFormat, masterClassesLeadsDownloadUrl, partnerCompanyLeadsDownloadUrl } from '@/services/ApiService'
import { useMeStore } from '@/stores'

import EventLeadsItem from './EventLeadsItem.vue'

const { locale, t } = useI18n()

const props = defineProps<{
  eventTypes: EventTypeEnum[]
  hasRootEvent: boolean
  slug: string
}>()

const meStore = useMeStore()
const { currentPartnerCompanyId, currentPartnerCompanyGlobalId } = storeToRefs(meStore)
const hasPartnerCompany = computed(() => !!currentPartnerCompanyId.value)

const rootEventSlug = computed(() => (props.hasRootEvent ? props.slug : null))
const rootEventName = computed(() => (props.hasRootEvent ? rootEvent.value?.name : 'Digital Masterclasses'))
const queryVars = computed<LeadCountsByEventQueryVariables>(() => {
  let vars: LeadCountsByEventQueryVariables = { type: props.eventTypes }
  if (props.hasRootEvent) {
    vars = { ...vars, rootEvents: [{ slug: rootEventSlug.value }] }
  }
  return vars
})
const { result, loading: eventsLoading } = useLeadCountsByEventQuery(() => queryVars.value)
const events = computed(() => result.value?.events?.nodes || [])

const { result: rootEventResult, loading: rootEventLoading } = useEventBySlugQuery(() => ({ slug: props.slug }), {
  enabled: props.hasRootEvent,
})
const rootEvent = computed(() => rootEventResult.value?.eventBySlug)

const loading = computed(() => eventsLoading.value || rootEventLoading.value || partnerCompanyLeadCountLoading.value)

type Event = NonNullable<NonNullable<LeadCountsByEventQuery['events']>['nodes']>[0]
const sumOfMasterclassLeads = computed(() =>
  events.value.map((event: Event) => event?.leadCount || 0).reduce((leadCount: number, sum: number) => leadCount + sum, 0),
)

const { result: partnerCompanyLeadCountResult, loading: partnerCompanyLeadCountLoading } = usePartnerCompanyLeadCountQuery(
  () => ({
    mainEventId: props.slug,
    partnerCompanyId: currentPartnerCompanyGlobalId.value as string,
  }),
  { enabled: props.hasRootEvent && hasPartnerCompany.value },
)
const partnerCompanyAppLeadCount = computed(() => partnerCompanyLeadCountResult.value?.leads?.totalCount || 0)

const sumOfLeads = computed(() => sumOfMasterclassLeads.value + partnerCompanyAppLeadCount.value)

function downloadAppLeadsUrl() {
  if (rootEvent.value?.id && hasPartnerCompany.value) {
    const plainEventId = toTypeAndId(rootEvent.value.id)['id'] as string
    return (format: exportFormat) => partnerCompanyLeadsDownloadUrl(plainEventId, locale.value, format)
  } else {
    return () => ''
  }
}
function downloadEventLeadsUrl(eventId: string) {
  const plainEventId = toTypeAndId(eventId)['id'] as string
  return (format: exportFormat) => masterClassesLeadsDownloadUrl(plainEventId, locale.value, format)
}
</script>
